import { z } from "zod";

import { amount, date, reference, text } from "@frontend/lib/form/validators";
import { msg } from "@lingui/core/macro";

export const amendmentSchema = z.object({
  id: z.number().int().nullable().default(null),
  dateBegin: date.default(null),
  price: amount.default(null),
  comments: z.string().nullable().default(null),
  deletable: z.boolean().default(true),
});

export const commercialConditionSchema = z.object({
  subscriptionPaymentPeriodId: reference.default(null),
  paymentTypeId: reference.default(null),
  paymentEmail: z.string().nullable().default(null),
  paymentTargetId: reference.default(null),
  subscriptionBillingTimeId: reference.default(null),
  priceRevisionActivation: z.boolean().default(true),
});

export const contractSchema = z
  .object({
    lockVersion: z.string().nullable().default(null),
    dateBegin: date.default(null),
    dateEnd: date.default(null),
    contractGroupId: reference.default(null),
    contractDurationId: reference.default(null),
    endDelayId: reference.default(null),
    billPeriodId: reference.default(null),
    postPeriodBilling: z.boolean().default(false),
    autoRenewal: z.boolean().default(true),
    renewalWarning: z.boolean().default(false),
    workedWeeksCount: z.number().int().min(1).max(52).nullable().default(null),
    terminationReasonId: reference.default(null),
    price: amount.default(null),
    priceType: z.enum(["hour", "flat"]).nullable().default(null),
    amendments: z.array(amendmentSchema).default([]),
    commercialCondition: commercialConditionSchema,
  })
  .superRefine(({ dateBegin, dateEnd, amendments }, ctx) => {
    if (dateBegin && dateEnd && dateBegin > dateEnd) {
      ctx.addIssue({
        code: "custom",
        message: text(msg`The end comes before the start`),
        path: ["dateEnd"],
      });
    }

    amendments.forEach((amendment, index) => {
      if (null === amendment.dateBegin) {
        ctx.addIssue({
          code: "custom",
          message: text(msg`Enter a date`),
          path: ["amendments", index, "dateBegin"],
        });
      }

      if (null === amendment.price || "" === amendment.price.trim()) {
        ctx.addIssue({
          code: "custom",
          message: text(msg`Enter an amount`),
          path: ["amendments", index, "price"],
        });
      }
    });
  });

export type AmendmentValues = z.infer<typeof amendmentSchema>;
export type CommercialConditionValues = z.infer<
  typeof commercialConditionSchema
>;
export type ContractValues = z.infer<typeof contractSchema>;
